import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  roundToHalf,
  roundToQuarter,
  orientationAndRatioPercentage,
  realNumber,
} from 'utils/math_util';
import { inRange, sortedUniq, clamp } from 'lodash';
import { selectZoomedPPI } from 'selectors/ppi';

import canvases from 'domains/canvases';
import activeEditor from 'domains/active_editor';
import products from 'domains/products';

import type { CustomSizeProductCustomOptions, ProductTemplateAPIData } from 'types/catalog_types';
import type { Dimension } from 'types/common_types';
import type { NextSizePayload } from 'domains/canvases';

type CanvasToolsProps = {
  canvasId: string;
  productWidth: number;
  productHeight: number;
  canvasCustomSize: Dimension;
  canvasRotation: { rotation: number; userRotated: boolean };
  canvasDimensions: Dimension;
  canvasProduct: ProductTemplateAPIData;
  isCustomProduct: boolean;
  handleProductResizeCanvasReposition: (x: number, y: number, nextSize: NextSizePayload) => void;
};

type NextSizesBySize = Record<number, NextSizePayload[]>;

type Anchor = {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
};

type ResizeDirection =
  | 'top-left'
  | 'top-middle'
  | 'top-right'
  | 'middle-right'
  | 'bottom-right'
  | 'bottom-middle'
  | 'bottom-left'
  | 'middle-left';

function CanvasTools(props: CanvasToolsProps) {
  const {
    canvasId,
    productWidth,
    productHeight,
    canvasCustomSize,
    canvasRotation,
    canvasDimensions,
    handleProductResizeCanvasReposition,
    canvasProduct,
    isCustomProduct,
  } = props;

  const dispatch = useDispatch();
  const ppi = useSelector(selectZoomedPPI);
  const filteredProducts = useSelector(activeEditor.selectors.filteredProducts);

  // Width values for resizing
  const [portraitsByWidth, setPortraitsByWidth] = useState<NextSizesBySize>({});
  const [landscapesByWidth, setLandscapesByWidth] = useState<NextSizesBySize>({});
  const [portraitWidths, setPortraitWidths] = useState<number[]>([]);
  const [landscapeWidths, setLandscapeWidths] = useState<number[]>([]);

  // Height values for resizing
  const [portraitsByHeight, setPortraitsByHeight] = useState<NextSizesBySize>({});
  const [landscapesByHeight, setLandscapesByHeight] = useState<NextSizesBySize>({});
  const [portraitHeights, setPortraitHeights] = useState<number[]>([]);
  const [landscapeHeights, setLandscapeHeights] = useState<number[]>([]);

  // Values for visual updates to the drag box
  const [resizeX, setResizeX] = useState(0);
  const [resizeY, setResizeY] = useState(0);
  const [anchorPoint, setAnchorPoint] = useState<Anchor>({});
  const [currentAspectRatio, setCurrentAspectRatio] = useState(0);

  // Values that update the product
  const [nextSize, setNextSize] = useState<NextSizePayload | null>(null);
  const [shouldUpdateSize, setShouldUpdateSize] = useState(false);

  // Reposition the canvas if drag resized to left or top
  const [shouldUpdateCanvasPositionX, setShouldUpdateCanvasPositionX] = useState(false);
  const [shouldUpdateCanvasPositionY, setShouldUpdateCanvasPositionY] = useState(false);

  const movementX = useRef(0);
  const movementY = useRef(0);

  // Style for resize dragbox
  const resizeContainerStyle = useMemo(() => {
    return {
      border: resizeX || resizeY ? '1px dotted black' : 'none',
      width: `calc(100% + ${resizeX}px`,
      height: `calc(100% + ${resizeY}px`,
      ...anchorPoint,
    };
  }, [resizeX, resizeY, anchorPoint]);

  // Style for highlighted next product size
  const nextSizeGuideStyle = useMemo(() => {
    if (nextSize && canvasProduct) {
      // only show box if we have a next size and it's not the same size as current
      const sameSize =
        nextSize.printX === parseFloat(canvasProduct.print_size_x) &&
        nextSize.printY === parseFloat(canvasProduct.print_size_y);

      if (nextSize.width && !sameSize) {
        return {
          width: nextSize.printX * ppi,
          height: nextSize.printY * ppi,
          ...anchorPoint,
        };
      }
    }

    return {
      display: 'none',
    };
  }, [nextSize, ppi, canvasProduct]);

  // Set the aspect ratio of the current product for when user constrains the resize
  useEffect(() => {
    if (canvasProduct) {
      const currentW =
        canvasRotation.rotation === 0 ? canvasProduct.print_size_x : canvasProduct.print_size_y;
      const currentH =
        canvasRotation.rotation === 0 ? canvasProduct.print_size_y : canvasProduct.print_size_x;

      setCurrentAspectRatio(roundToHalf(parseFloat(currentW)) / roundToHalf(parseFloat(currentH)));
    }
  }, [canvasProduct, canvasRotation]);

  // Set up the data structures to make resizing by drag box more efficient
  // Here we set objects [orientation]By[Height/Width] and arrays [orientation][Heights/Widths], which is sorted & unique'd
  useEffect(() => {
    const portraitWs: number[] = [];
    const portraitHs: number[] = [];
    const landscapeWs: number[] = [];
    const landscapeHs: number[] = [];

    const pByW: NextSizesBySize = {};
    const pByH: NextSizesBySize = {};

    const lByW: NextSizesBySize = {};
    const lByH: NextSizesBySize = {};

    // TODO: this whole thing can probably be done much more efficiently in redux
    // For all the resizable products, group them by orientation and size
    // Doing in all in a single foreach to reduce computations
    filteredProducts.forEach((s) => {
      let { print_size_x: printXStr, print_size_y: printYStr } = s;
      const printX = parseFloat(printXStr);
      const printY = parseFloat(printYStr);
      const { orientation } = orientationAndRatioPercentage(printX, printY);

      // Set portraitsBy[Height/Width]
      const portraitW = orientation === 'landscape' ? roundToHalf(printY) : roundToHalf(printX);
      const portraitH = orientation === 'landscape' ? roundToHalf(printX) : roundToHalf(printY);
      const portraitSize = {
        product: s,
        printX: orientation === 'landscape' ? printY : printX,
        printY: orientation === 'landscape' ? printX : printY,
        width: portraitW,
        height: portraitH,
        rotate: orientation !== 'portrait',
      };

      pByH[portraitH] = pByH[portraitH] ? pByH[portraitH].concat([portraitSize]) : [portraitSize];
      pByW[portraitW] = pByW[portraitW] ? pByW[portraitW].concat([portraitSize]) : [portraitSize];

      // Set landscapesBy[Height/Width]
      const landscapeW = orientation === 'landscape' ? roundToHalf(printX) : roundToHalf(printY);
      const landscapeH = orientation === 'landscape' ? roundToHalf(printY) : roundToHalf(printX);
      const landscapeSize = {
        product: s,
        printX: orientation === 'landscape' ? printX : printY,
        printY: orientation === 'landscape' ? printY : printX,
        width: landscapeW,
        height: landscapeH,
        rotate: orientation !== 'landscape',
      };

      lByH[landscapeH] = lByH[landscapeH]
        ? lByH[landscapeH].concat([landscapeSize])
        : [landscapeSize];
      lByW[landscapeW] = lByW[landscapeW]
        ? lByW[landscapeW].concat([landscapeSize])
        : [landscapeSize];

      // Save the heights and widths to sort & uniq later
      portraitWs.push(portraitW);
      portraitHs.push(portraitH);
      landscapeWs.push(landscapeW);
      landscapeHs.push(landscapeH);
    });

    setPortraitsByWidth(pByW);
    setLandscapesByWidth(lByW);

    setPortraitsByHeight(pByH);
    setLandscapesByHeight(lByH);

    // Sort and dedupe the widths and heights
    const sortedPortraitWidths = sortedUniq(portraitWs.sort((a, b) => a - b));
    const sortedPortraitHeights = sortedUniq(portraitHs.sort((a, b) => a - b));

    setPortraitWidths(sortedPortraitWidths);
    setPortraitHeights(sortedPortraitHeights);

    // These are the same, just flipped from portrait
    setLandscapeWidths(sortedPortraitHeights);
    setLandscapeHeights(sortedPortraitWidths);
  }, [filteredProducts]);

  // Sets the new product if the user resized to a matching size then released mouse drag
  useEffect(() => {
    if (nextSize?.product && shouldUpdateSize) {
      if (isCustomProduct) {
        dispatch(
          canvases.actions.changeActiveCanvasConfig.size({
            height: nextSize.height,
            width: nextSize.width,
          })
        );
      } else {
        dispatch(canvases.actions.changeActiveCanvasConfig.product(nextSize.product.id));

        // Rotates the product to the right orientation to match the user's drag box
        if (
          nextSize.product.rotation_allowed &&
          !products.utils.productIsMultiNode(nextSize.product)
        ) {
          dispatch(
            canvases.actions.updateCanvas({
              rotation: nextSize.rotate ? 90 : 0,
              userRotated: true,
            })
          );
        }
      }

      // Match canvas position to the point the resized product starts
      const xMove = shouldUpdateCanvasPositionX
        ? nextSize.printX * ppi - canvasDimensions.width
        : 0;
      const yMove = shouldUpdateCanvasPositionY
        ? nextSize.printY * ppi - canvasDimensions.height
        : 0;
      handleProductResizeCanvasReposition(xMove, yMove, nextSize);

      setShouldUpdateCanvasPositionX(false);
      setShouldUpdateCanvasPositionY(false);
      setShouldUpdateSize(false);
      setNextSize(null);
    }
  }, [
    nextSize,
    shouldUpdateSize,
    shouldUpdateCanvasPositionX,
    shouldUpdateCanvasPositionY,
    canvasDimensions,
    ppi,
    isCustomProduct,
  ]);

  // Get the canvas product orientation & rotation, flip the numbers in the name to match
  const currentDisplaySize = useMemo(() => {
    if (isCustomProduct) {
      const { width, height } = canvasCustomSize;
      return { width, height };
    }

    const [firstSize, secondSize] = canvasProduct.name.split('x');
    const { orientation } = orientationAndRatioPercentage(productWidth, productHeight);
    const { rotation } = canvasRotation;
    const defaultPortrait =
      (orientation === 'portrait' && rotation === 0) ||
      (orientation === 'landscape' && rotation === 90);

    if (
      orientation === 'square' ||
      (defaultPortrait && rotation === 0) ||
      (!defaultPortrait && rotation === 90)
    ) {
      return { width: firstSize, height: secondSize };
    }

    return { width: secondSize, height: firstSize };
  }, [
    canvasProduct,
    canvasRotation,
    productHeight,
    productWidth,
    isCustomProduct,
    canvasCustomSize,
  ]);

  function setResizeWithDirection(
    x: number,
    y: number,
    direction: ResizeDirection,
    constrainRatio: boolean
  ) {
    const [yDirection, xDirection] = direction.split('-');
    const anchor: Anchor = {};
    const shouldConstrainRatio =
      constrainRatio && yDirection !== 'middle' && xDirection !== 'middle';

    switch (xDirection) {
      case 'left':
        movementX.current -= x;
        movementY.current = shouldConstrainRatio
          ? movementX.current / currentAspectRatio
          : movementY.current;
        anchor.right = 0;
        setShouldUpdateCanvasPositionX(true);
        break;
      case 'right':
        movementX.current += x;
        movementY.current = shouldConstrainRatio
          ? movementX.current / currentAspectRatio
          : movementY.current;
        anchor.left = 0;
        break;
      // No x direction changes if moving from middle
      case 'middle':
      default:
        break;
    }

    switch (yDirection) {
      case 'top':
        movementY.current = shouldConstrainRatio ? movementY.current : movementY.current - y;
        anchor.bottom = 0;
        setShouldUpdateCanvasPositionY(true);
        break;
      case 'bottom':
        movementY.current = shouldConstrainRatio ? movementY.current : movementY.current + y;
        anchor.top = 0;
        break;
      // No y direction changes if moving from middle
      case 'middle':
      default:
        break;
    }

    // Set the values that update dragging box size
    setAnchorPoint(anchor);
    setResizeX(movementX.current);
    setResizeY(movementY.current);

    return {
      shouldConstrainRatio,
      resizedH: productHeight + movementY.current / ppi,
      resizedW: productWidth + movementX.current / ppi,
      resizedCustomH: canvasCustomSize.height + movementY.current / ppi,
      resizedCustomW: canvasCustomSize.width + movementX.current / ppi,
    };
  }

  function findClosestProductSize(
    resizedW: number,
    resizedH: number,
    shouldConstrainRatio: boolean
  ) {
    const { orientation } = orientationAndRatioPercentage(resizedW, resizedH);

    // Select the object for lookups by the dragbox orientation
    const productsByWidth = orientation === 'landscape' ? landscapesByWidth : portraitsByWidth;
    const productsByHeight = orientation === 'landscape' ? landscapesByHeight : portraitsByHeight;

    // Select the available heights/widths by the dragbox orientation
    const productWidths = orientation === 'landscape' ? landscapeWidths : portraitWidths;
    const productHeights = orientation === 'landscape' ? landscapeHeights : portraitHeights;

    let closestWidth = productsByWidth[resizedW] ? resizedW : null;
    let closestHeight = productsByHeight[resizedH] ? resizedH : null;

    if (!closestWidth) {
      closestWidth = productWidths.reduce((acc, width) => {
        const difference = Math.abs(resizedW - width);
        const previousDifference = Math.abs(resizedW - acc);

        if (difference < previousDifference) {
          return width;
        }

        return acc;
      });
    }

    if (!closestHeight) {
      closestHeight = productHeights.reduce((acc, height) => {
        const difference = Math.abs(resizedH - height);
        const previousDifference = Math.abs(resizedH - acc);

        if (difference < previousDifference) {
          return height;
        }

        return acc;
      });
    }

    // Find a matching product height within 0.5in for the closest width
    const firstClosestProduct = productsByWidth[closestWidth].find((closestProduct) => {
      const { printY } = closestProduct;
      return inRange(resizedH, printY - 0.5, printY + 0.5);
    });

    // Find a matching product width within 0.5in for the closest height
    const firstClosestProductH = productsByHeight[closestHeight].find((closestProduct) => {
      const { printX } = closestProduct;
      return inRange(resizedW, printX - 0.5, printX + 0.5);
    });

    if (firstClosestProduct) {
      if (shouldConstrainRatio) {
        if (firstClosestProduct.width / firstClosestProduct.height === currentAspectRatio) {
          return firstClosestProduct;
        }
      } else {
        return firstClosestProduct;
      }
    }

    if (firstClosestProductH) {
      if (shouldConstrainRatio) {
        if (firstClosestProductH.width / firstClosestProductH.height === currentAspectRatio) {
          return firstClosestProductH;
        }
      } else {
        return firstClosestProductH;
      }
    }

    // If we get here, next size will be empty
    return null;
  }

  function findNextCustomSize(width: number, height: number): NextSizePayload {
    let nextW;
    let nextH;
    // This is only called when we're sure we're on a custom size, so safe to cast this
    let { bleed, frame, wrap } = canvasProduct.custom_options as CustomSizeProductCustomOptions;
    const { longmin, longmax, shortmin, shortmax } =
      canvasProduct.custom_options as CustomSizeProductCustomOptions;
    const { orientation } = orientationAndRatioPercentage(width, height);

    if (orientation === 'landscape') {
      nextW = clamp(roundToQuarter(width), longmin, longmax);
      nextH = clamp(roundToQuarter(height), shortmin, shortmax);
    } else {
      nextW = clamp(roundToQuarter(width), shortmin, shortmax);
      nextH = clamp(roundToQuarter(height), longmin, longmax);
    }

    // multiply by 2 to account for both sides
    bleed = (bleed && realNumber(bleed) && bleed * 2) || 0;
    frame = (frame && realNumber(frame) && frame * 2) || 0;
    wrap = (wrap && realNumber(wrap) && wrap * 2) || 0;

    return {
      product: canvasProduct,
      printX: nextW + bleed + frame + wrap,
      printY: nextH + bleed + frame + wrap,
      width: nextW,
      height: nextH,
      rotate: false,
    };
  }

  function handleDragResize(direction: ResizeDirection) {
    function trackMovement(e: MouseEvent) {
      const { shouldConstrainRatio, resizedH, resizedW, resizedCustomH, resizedCustomW } =
        setResizeWithDirection(e.movementX, e.movementY, direction, e.shiftKey);

      let next;

      if (isCustomProduct) {
        next = findNextCustomSize(resizedCustomW, resizedCustomH);
      } else {
        next = findClosestProductSize(resizedW, resizedH, shouldConstrainRatio);
      }

      if (next?.width) {
        setNextSize(next);
      }
    }

    function cancelResize(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        document.removeEventListener('mousemove', trackMovement);
        document.removeEventListener('keydown', cancelResize);
        resetResize();
        setNextSize(null);
        setShouldUpdateSize(false);
      }
    }

    function saveResize() {
      document.removeEventListener('keydown', cancelResize);
      document.removeEventListener('mousemove', trackMovement);
      setShouldUpdateSize(true);
      resetResize();
    }

    function resetResize() {
      movementX.current = 0;
      movementY.current = 0;
      setResizeX(0);
      setResizeY(0);
      setAnchorPoint({});
    }

    document.addEventListener('mousemove', trackMovement);
    document.addEventListener('mouseup', saveResize, { once: true });
    document.addEventListener('keydown', cancelResize);
  }

  function handleTouchResize(direction: ResizeDirection) {
    let previousTouch: Touch | undefined;

    function trackMovement(e: TouchEvent) {
      e.preventDefault();
      const touch = e.touches[0];
      let moveX;
      let moveY;

      if (previousTouch) {
        moveY = touch.pageX - previousTouch.pageX;
        moveX = touch.pageY - previousTouch.pageY;
        previousTouch = touch;
      } else {
        previousTouch = touch;
        return;
      }

      const { shouldConstrainRatio, resizedH, resizedW, resizedCustomH, resizedCustomW } =
        setResizeWithDirection(moveX, moveY, direction, false);

      let next;

      if (isCustomProduct) {
        next = findNextCustomSize(resizedCustomW, resizedCustomH);
      } else {
        next = findClosestProductSize(resizedW, resizedH, shouldConstrainRatio);
      }

      if (next?.width) {
        setNextSize(next);
      }
    }

    function saveResize() {
      previousTouch = undefined;
      document.removeEventListener('touchmove', trackMovement);
      setShouldUpdateSize(true);
      resetResize();
    }

    function resetResize() {
      movementX.current = 0;
      movementY.current = 0;
      setResizeX(0);
      setResizeY(0);
      setAnchorPoint({});
    }

    document.addEventListener('touchmove', trackMovement, { passive: false });
    document.addEventListener('touchend', saveResize, { once: true });
  }

  return (
    <div className="wall-preview-canvas-tools" style={{ zIndex: resizeX || resizeY ? 3000 : 1 }}>
      {(nextSize?.width || currentDisplaySize.width) && (
        <span className="canvas-display-size">
          {nextSize?.width || currentDisplaySize.width}w x{' '}
          {nextSize?.height || currentDisplaySize.height}h
        </span>
      )}

      <div className="next-size-guide" style={nextSizeGuideStyle} />

      <div className="resize-canvas-container" style={resizeContainerStyle}>
        {[
          'top-left',
          'top-middle',
          'top-right',
          'middle-right',
          'bottom-right',
          'bottom-middle',
          'bottom-left',
          'middle-left',
        ].map((direction: ResizeDirection) => {
          return (
            <button
              key={direction}
              className={`resize-canvas-handle ${direction}`}
              onMouseDown={() => {
                dispatch(canvases.actions.setActiveCanvasId(canvasId));
                handleDragResize(direction);
              }}
              onTouchStart={() => {
                dispatch(canvases.actions.setActiveCanvasId(canvasId));
                handleTouchResize(direction);
              }}
            >
              <span className="screen-reader-text">Resize product</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default CanvasTools;
